// Phrases — frases útiles del viaje actual (idiomas o expresiones locales)
import { Link } from "react-router-dom";
import { ChevronLeft, Volume2 } from "lucide-react";
import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { useTripBase } from "../hooks/useTripBase";
import { useTrip } from "../auth/TripContext";

export default function Phrases() {
  const base = useTripBase();
  const { trip } = useTrip();
  const phrasesData = trip?.phrases || {};
  const LANGS = useMemo(() => Object.keys(phrasesData).map((k) => ({
    key: k, label: `${phrasesData[k]?.flag || "🌐"} ${k.replace("es-", "").replace("-", " ")}`,
  })), [phrasesData]);
  const [lang, setLang] = useState(LANGS[0]?.key || "");
  const data = phrasesData[lang];

  // iOS Web Speech para reproducir pronunciación
  const speak = (text, code) => {
    if (!("speechSynthesis" in window)) return;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = code || "es-ES";
    u.rate = 0.85;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
  };

  if (!data || !LANGS.length) {
    return (
      <div className="container-app pt-6 pb-28">
        <Link to={base || "/"} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-4">
          <ChevronLeft size={16} /> Inicio
        </Link>
        <h1 className="text-3xl font-bold leading-tight">Frases útiles</h1>
        <p className="text-sm text-[hsl(var(--muted))] mt-6 text-center">Este viaje aún no tiene frases cargadas.</p>
      </div>
    );
  }

  return (
    <div className="container-app pt-6 pb-28" data-testid="phrases-page">
      <Link to={base || "/"} className="inline-flex items-center gap-1 text-sm text-[hsl(var(--muted))] mb-4" data-testid="phrases-back">
        <ChevronLeft size={16} /> Inicio
      </Link>
      <h1 className="text-3xl font-bold leading-tight">Frases útiles</h1>
      <p className="text-sm text-[hsl(var(--muted))] mt-1 mb-5">Expresiones locales y vocabulario cultural</p>

      {LANGS.length > 1 && (
        <div className="flex gap-2 mb-5 overflow-x-auto hide-scrollbar">
          {LANGS.map((l) => (
            <button
              key={l.key}
              onClick={() => setLang(l.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                lang === l.key ? "bg-[hsl(var(--ink))] text-[hsl(var(--bg))]" : "bg-[hsl(var(--bg-card))] text-[hsl(var(--ink))] border border-[hsl(var(--line))]"
              }`}
              data-testid={`phrases-lang-${l.key}`}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}

      {data.note && (
        <div className="card-paper p-4 mb-5">
          <p className="text-sm text-[hsl(var(--ink-soft))] leading-relaxed">{data.note}</p>
        </div>
      )}

      {/* Andalucismos list (new format) */}
      {Array.isArray(data.andalucismos) && data.andalucismos.length > 0 && (
        <div className="space-y-3 mb-5">
          {data.andalucismos.map((a) => (
            <div key={a.id} className="card-paper p-4" data-testid={`phrase-${a.id}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <p className="font-bold text-base">"{a.term}"</p>
                {a.city && <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted))]">{a.city}</span>}
              </div>
              <p className="text-sm text-[hsl(var(--ink-soft))] leading-snug">{a.meaning}</p>
              {a.example && <p className="text-xs italic text-[hsl(var(--muted))] mt-2">Ej: {a.example}</p>}
              {a.tone && <span className="inline-block mt-2 text-[10px] px-2 py-0.5 rounded-full bg-[hsl(var(--bg-card))] border border-[hsl(var(--line))]">{a.tone}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Legacy categories */}
      {(data.categories || []).map((cat, ci) => (
        <motion.section
          key={cat.name}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 * ci }}
          className="mb-6"
        >
          <h3 className="section-title mb-3">{cat.name}</h3>
          <ul className="space-y-2">
            {cat.items.map((p, i) => (
              <li key={i} className="card-paper p-3 flex items-start gap-3" data-testid={`phrase-${cat.name}-${i}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[hsl(var(--muted))] uppercase tracking-wider font-semibold">{p.es}</p>
                  <p className="text-base font-semibold serif mt-0.5">{p.local}</p>
                  <p className="text-xs text-[hsl(var(--accent))] italic mt-0.5">/{p.phon}/</p>
                </div>
                <button
                  onClick={() => speak(p.local, lang === "Hungarian" ? "hu-HU" : "cs-CZ")}
                  className="shrink-0 w-9 h-9 rounded-full bg-[hsl(var(--bg))] border border-[hsl(var(--line))] flex items-center justify-center active:scale-95"
                  data-testid={`phrase-speak-${i}`}
                  aria-label="Escuchar"
                >
                  <Volume2 size={16} className="text-[hsl(var(--ink))]" />
                </button>
              </li>
            ))}
          </ul>
        </motion.section>
      ))}
    </div>
  );
}
